// Pure grouping for the admin company detail page: questionnaire tabs with
// category sections, the Old Questionnaires bucket, and the Compliance
// Documents rows. Kept free of React (same pattern as wizard.ts) so the tab
// model can be unit-tested directly — the app has no DOM test environment.

import { orderWithInstances } from './wizard.ts';
import type { AdminQuestionnaire, Question, QuestionCategory } from './types.ts';

export type CategorySection = { category: QuestionCategory; questions: Question[] };

export type QuestionnaireGroup = {
  questionnaire: AdminQuestionnaire;
  sections: CategorySection[];
};

export type ComplianceDocRow = { question: Question; categoryName: string };

export type CompanyDetailTabs = {
  /** Applicable questionnaires with at least one visible non-doc question, in display_order. */
  questionnaireTabs: QuestionnaireGroup[];
  /** Non-applicable questionnaires with at least one answered non-doc question. */
  oldGroups: QuestionnaireGroup[];
  /** Every compliance-flagged question to review, in questionnaire → category → question order (deduped). */
  complianceDocs: ComplianceDocRow[];
  /** Visible non-doc questions whose category row is missing (data-integrity fallback). */
  orphanQuestions: Question[];
};

// Label shown in the Compliance Documents table for questions whose category
// row no longer exists.
export const UNKNOWN_CATEGORY_LABEL = '—';

// Membership in the Compliance Documents tab is driven by an explicit
// per-question flag (migration 0030), replacing the old "type === document_upload"
// heuristic. Existing document_upload questions were backfilled to true.
function isComplianceDocQuestion(q: Question): boolean {
  return q.include_in_compliance_documents;
}

/**
 * The question set the admin page's progress line counts: questions whose
 * questionnaire currently applies, plus true orphans (missing category row) so
 * an orphaned required question still counts and stays reachable. Questions in
 * non-applicable or non-company questionnaires are excluded — matching the
 * company portal's server-side progress computation (which filters by
 * questionnaire_id, migration 0032).
 *
 * Assumes parent/child (condition) links never cross questionnaires, so the
 * returned set stays closed under parent_id and downstream visibility checks
 * can always resolve a question's parent.
 */
export function questionsForProgress(
  questionnaires: AdminQuestionnaire[],
  categories: QuestionCategory[],
  questions: Question[],
): Question[] {
  const applicable = new Set(questionnaires.filter((x) => x.applicable).map((x) => x.id));
  const catById = new Map(categories.map((c) => [c.id, c]));
  return questions.filter((q) => {
    if (!catById.has(q.category_id)) return true; // orphan: keep counted & reachable
    // A question belongs to exactly one questionnaire (migration 0032); count it
    // when that questionnaire currently applies to the company.
    return q.questionnaire_id !== null && applicable.has(q.questionnaire_id);
  });
}

/**
 * Group questions for the company detail tabs.
 *
 * - Applicable questionnaires contribute visible questions; non-applicable
 *   ones contribute only answered questions (visibility is not re-evaluated
 *   for retired questionnaires — what was answered is shown).
 * - compliance-flagged questions go to `complianceDocs` (with their category
 *   name); everything else stays in its category section.
 * - A question belongs to exactly one questionnaire (its questionnaire_id,
 *   migration 0032) and renders only there, even when its category is a shared
 *   grouping ticked into several questionnaires. The category supplies the
 *   sub-tab (a category may appear under more than one questionnaire), but each
 *   questionnaire shows only its own questions.
 * - Questions whose questionnaire_id is null or not a known questionnaire (e.g.
 *   another audience) are dropped; questions that claim a known questionnaire
 *   but whose category row is missing or isn't linked there surface as orphans
 *   so they are never silently unreachable.
 */
export function buildCompanyDetailTabs(
  questionnaires: AdminQuestionnaire[],
  categories: QuestionCategory[],
  questions: Question[],
  visibleIds: Set<string>,
  answeredIds: Set<string>,
): CompanyDetailTabs {
  const catById = new Map(categories.map((c) => [c.id, c]));
  const questionnaireById = new Map(questionnaires.map((x) => [x.id, x]));

  // Categories per questionnaire in display_order (the API orders them, but
  // sort defensively so grouping never depends on fetch order). A shared
  // category is listed under every known questionnaire it is linked to.
  const catsByQuestionnaire = new Map<string, QuestionCategory[]>();
  for (const c of [...categories].sort((a, b) => a.display_order - b.display_order)) {
    for (const questionnaireId of c.questionnaire_ids) {
      if (!questionnaireById.has(questionnaireId)) continue;
      const list = catsByQuestionnaire.get(questionnaireId) ?? [];
      list.push(c);
      catsByQuestionnaire.set(questionnaireId, list);
    }
  }

  // Questions bucketed by their (questionnaire, category) home, with per-option
  // mirrors seated next to their authored row (same ordering the wizard and the
  // old per-category tabs used). A question renders only under its own
  // questionnaire_id (migration 0032), so a shared category shows different
  // questions in each questionnaire it is ticked into.
  const questionsByQnCat = new Map<string, Map<string, Question[]>>();
  const orphanPool: Question[] = [];
  for (const question of orderWithInstances(questions)) {
    const qn = question.questionnaire_id ? questionnaireById.get(question.questionnaire_id) : undefined;
    if (!qn) continue; // belongs to no known questionnaire (other audience / unassigned)
    const cat = catById.get(question.category_id);
    if (!cat || !cat.questionnaire_ids.includes(qn.id)) {
      orphanPool.push(question); // claims a known questionnaire but category missing / not linked there
      continue;
    }
    const byCat = questionsByQnCat.get(qn.id) ?? new Map<string, Question[]>();
    const list = byCat.get(cat.id) ?? [];
    list.push(question);
    byCat.set(cat.id, list);
    questionsByQnCat.set(qn.id, byCat);
  }

  const questionnaireTabs: QuestionnaireGroup[] = [];
  const oldGroups: QuestionnaireGroup[] = [];
  const complianceDocs: ComplianceDocRow[] = [];
  const seenComplianceIds = new Set<string>();

  const ordered = [...questionnaires].sort((a, b) => a.display_order - b.display_order);
  for (const questionnaire of ordered) {
    // Applicable questionnaires show what the company can currently see;
    // retired ones show what was actually answered.
    const include = questionnaire.applicable
      ? (q: Question) => visibleIds.has(q.id)
      : (q: Question) => answeredIds.has(q.id);

    const byCat = questionsByQnCat.get(questionnaire.id);
    const sections: CategorySection[] = [];
    for (const category of catsByQuestionnaire.get(questionnaire.id) ?? []) {
      const nonDoc: Question[] = [];
      for (const question of (byCat?.get(category.id) ?? []).filter(include)) {
        if (isComplianceDocQuestion(question)) {
          if (!seenComplianceIds.has(question.id)) {
            seenComplianceIds.add(question.id);
            complianceDocs.push({ question, categoryName: category.name });
          }
        } else {
          nonDoc.push(question);
        }
      }
      if (nonDoc.length > 0) sections.push({ category, questions: nonDoc });
    }

    // A questionnaire whose remaining questions are all compliance docs gets no
    // tab — its documents still surface in the Compliance Documents table.
    if (sections.length === 0) continue;
    (questionnaire.applicable ? questionnaireTabs : oldGroups).push({ questionnaire, sections });
  }

  // Orphans render under a trailing "Additional questions" tab; orphan
  // compliance-flagged questions still join the Compliance Documents table.
  const visibleOrphans = orphanPool.filter((q) => visibleIds.has(q.id));
  for (const question of visibleOrphans) {
    if (isComplianceDocQuestion(question) && !seenComplianceIds.has(question.id)) {
      seenComplianceIds.add(question.id);
      complianceDocs.push({ question, categoryName: UNKNOWN_CATEGORY_LABEL });
    }
  }

  return {
    questionnaireTabs,
    oldGroups,
    complianceDocs,
    // Compliance-flagged orphans were already pushed to complianceDocs above.
    orphanQuestions: visibleOrphans.filter((q) => !isComplianceDocQuestion(q)),
  };
}
